import type Context from '@/context';
import type { IChipEditorModel, IChipModel, ICircuitModel } from '@/interfaces/models';
import { createPasteAction } from '@/actions/pasteAction';
import { allowsChallengePlacement, allowsChallengeTile } from '@/challenges/editing';
import { restrictChallengeInterfacePaste } from '@/challenges/clipboardInterfaces';
import type {
  ClipboardData,
  ClipboardConnectionPoint,
} from '@/models/clipboard/ClipboardData';
import { parseClipChipRef } from '@/models/clipboard/ClipboardData';
import {
  Rotation,
  composeRotation,
  rotateSide,
  rotatePointAroundOrigin,
} from '@/models/data/Rotation';
import type { PointData } from '@/models/Point';
import { Point } from '@/models/Point';
import { isValidTilePosition } from '@/models/TileManagerModel';
import { TileType } from '@/models/TileTypes';
import { isInterfaceTileDefinition } from '@/models/tiles/TileDefinition';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { CircuitType } from '@/schemas/common';
import { copySelection } from '@/services/ClipboardService';
import { deepClone } from '@/utils/clone';
import { getChipSelectability } from '@/utils/chipPlacement';

export type ProjectTemplateType = 'logic' | 'structure';

export const TEMPLATE_COLLISION_MESSAGE = 'The template overlaps existing tiles.';

export const templateCircuitType = (type: ProjectTemplateType): CircuitType =>
  type === 'logic' ? CircuitType.ChipLogic : CircuitType.ChipStructure;

export const templateGridMismatchMessage = (type: ProjectTemplateType): string =>
  `${type === 'logic' ? 'Logic' : 'Structure'} templates can only be placed in ${type === 'logic' ? 'Logic' : 'Structure'}.`;

/** Legacy templates anchor their bounding top-left, matching ordinary paste. */
export function templateDefaultOrigin(chip: IChipModel): PointData {
  const tiles = (
    chip.templateType === 'structure' ? chip.structureCircuit : chip.logicCircuit
  ).circuitModel.tiles;
  let x = Infinity,
    y = Infinity;
  for (const tile of tiles) {
    x = Math.min(x, tile.location.x);
    y = Math.min(y, tile.location.y);
  }
  return tiles.length ? { x, y } : { x: 0, y: 0 };
}

export function templatePasteOrigin(data: ClipboardData, cursor: PointData): PointData {
  return { x: cursor.x - (data.origin?.x ?? 0), y: cursor.y - (data.origin?.y ?? 0) };
}

/** Persistent captures never retain a cut identity or connections outside the capture. */
export function sanitizeTemplateClipboard(data: ClipboardData): ClipboardData {
  const result = deepClone(data);
  result.operation = 'copy';
  result.edgeHints = [];
  const positions = new Set(
    result.tiles.map(
      (entry) => `${entry.relativeLocation.x},${entry.relativeLocation.y}`,
    ),
  );
  const inside = (point: PointData) => positions.has(`${point.x},${point.y}`);
  result.connections = result.connections.filter(
    (edge) => inside(edge.a) && inside(edge.b),
  );
  if (result.disconnected)
    result.disconnected = result.disconnected.filter(
      (edge) => inside(edge.a) && inside(edge.b),
    );

  // A selected part of a bundle becomes its own local physical network. Stable
  // signal names/selections survive, but an unselected source cannot own it.
  const ids = new Set(result.tiles.map((entry) => entry.sourceTileId).filter(Boolean));
  const replacementOwners = new Map<string, string>();
  result.tiles.forEach((entry, index) => {
    const origin = entry.meta.bundle?.signalOrigin;
    if (!origin || ids.has(origin)) return;
    entry.sourceTileId ??= `template-tile:${index}`;
    if (!replacementOwners.has(origin)) replacementOwners.set(origin, entry.sourceTileId);
  });
  for (const entry of result.tiles) {
    const bundle = entry.meta.bundle;
    if (bundle?.signalOrigin && replacementOwners.has(bundle.signalOrigin))
      bundle.signalOrigin = replacementOwners.get(bundle.signalOrigin)!;
  }
  return result;
}

export function captureProjectTemplate(chip: IChipModel): ClipboardData | null {
  if (!chip.templateType) return null;
  const circuit =
    chip.templateType === 'logic'
      ? chip.logicCircuit.circuitModel
      : chip.structureCircuit.circuitModel;
  const tiles = circuit.tileManagerModel.tiles;
  if (!tiles.length) return null;
  const bounds = { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity };
  for (const tile of tiles) {
    bounds.left = Math.min(bounds.left, tile.location.x);
    bounds.right = Math.max(bounds.right, tile.location.x);
    bounds.top = Math.min(bounds.top, tile.location.y);
    bounds.bottom = Math.max(bounds.bottom, tile.location.y);
  }
  const captured = copySelection(
    bounds,
    circuit.tileManagerModel,
    circuit.connectionModel,
    circuit.chips,
  );
  if (!captured) return null;
  if (chip.templateOrigin)
    captured.origin = {
      x: chip.templateOrigin.x - bounds.left,
      y: chip.templateOrigin.y - bounds.top,
    };
  // Copying a saved template while another editor is active cannot resolve its
  // standalone link settings through the active editor. Resolve from its owner.
  for (const entry of captured.tiles) {
    if (entry.meta.partOfChip) continue;
    const id = (
      entry.state as { connectedInterface?: { interfaceLinkId?: string } } | undefined
    )?.connectedInterface?.interfaceLinkId;
    const link = id && chip.interfaceLinks.find((candidate) => candidate.id === id);
    if (link) entry.interfaceLink = deepClone(link.dto);
  }
  return sanitizeTemplateClipboard(captured);
}

/** Rotate the arrangement and each tile's local connection frame together. */
export function rotateTemplateClipboard(
  data: ClipboardData,
  clockwise = true,
): ClipboardData {
  const result = deepClone(data);
  const rotation = clockwise ? Rotation.CW_90 : Rotation.CW_270;
  const transform = (point: PointData): PointData =>
    clockwise
      ? { x: data.height - 1 - point.y, y: point.x }
      : { x: point.y, y: data.width - 1 - point.x };
  const fixedFrames = new Set(
    data.tiles
      .filter(
        (entry) =>
          !(
            entry.meta.partOfChip ||
            tileDefinitionRegistry.get(entry.tileType).canRotate ||
            entry.tileType === TileType.Wire ||
            entry.tileType === TileType.Bridge
          ),
      )
      .map((entry) => `${entry.relativeLocation.x},${entry.relativeLocation.y}`),
  );
  const endpoint = (point: ClipboardConnectionPoint): ClipboardConnectionPoint => ({
    ...transform(point),
    side: fixedFrames.has(`${point.x},${point.y}`)
      ? rotateSide(point.side, rotation)
      : point.side,
  });
  if (data.origin) result.origin = transform(data.origin);
  result.width = data.height;
  result.height = data.width;
  for (const entry of result.tiles) {
    if (!fixedFrames.has(`${entry.relativeLocation.x},${entry.relativeLocation.y}`))
      entry.meta.rotation = composeRotation(
        entry.meta.rotation ?? Rotation.CW_0,
        rotation,
      );
    entry.relativeLocation = transform(entry.relativeLocation);
  }
  result.connections = result.connections.map((edge) => ({
    a: endpoint(edge.a),
    b: endpoint(edge.b),
  }));
  if (result.disconnected)
    result.disconnected = result.disconnected.map((edge) => ({
      a: endpoint(edge.a),
      b: endpoint(edge.b),
    }));
  result.edgeHints = result.edgeHints.map((hint) => ({
    relativeLocation: transform(hint.relativeLocation),
    side: fixedFrames.has(`${hint.relativeLocation.x},${hint.relativeLocation.y}`)
      ? rotateSide(hint.side, rotation)
      : hint.side,
  }));
  for (const chip of result.chipInstances) {
    chip.relativeOrigin = transform(chip.relativeOrigin);
    chip.rotation = composeRotation(chip.rotation ?? Rotation.CW_0, rotation);
  }
  return result;
}

export function getTemplatePlacementError(
  context: Context,
  data: ClipboardData,
  origin: PointData,
  circuit: ICircuitModel,
  type: ProjectTemplateType,
  editingChip: IChipModel = context.editor.currentChip,
): string | null {
  if (circuit.circuitType !== templateCircuitType(type))
    return templateGridMismatchMessage(type);
  if (context.challenge && !context.challengeAuthoring)
    return 'Templates are unavailable while playing a challenge.';
  if (!data.tiles.length) return 'This template is empty.';
  origin = templatePasteOrigin(data, origin);
  if (
    type === 'structure' &&
    (data.chipInstances.length ||
      data.tiles.some(
        (entry) =>
          entry.meta.partOfChip ||
          (entry.tileType !== TileType.Structure &&
            !isInterfaceTileDefinition(tileDefinitionRegistry.get(entry.tileType))),
      ))
  )
    return 'Structure templates can contain only Structure tiles and interfaces.';
  if (
    type === 'structure' &&
    editingChip.kind === 'bundle-adapter' &&
    data.tiles.some(
      (entry) =>
        entry.tileType !== TileType.Structure &&
        entry.tileType !== TileType.SingleInterface &&
        entry.tileType !== TileType.BundleInterface,
    )
  )
    return 'Bundle adapters accept only Structure, Single Interface, and Bundle Interface tiles.';
  const indices = new Set(data.chipInstances.map((ref) => ref.localIndex));
  if (indices.size !== data.chipInstances.length)
    return 'The template contains duplicate chip references.';
  for (const ref of data.chipInstances) {
    const chip = context.chipStore.getChip(ref.chipId);
    if (!chip)
      return 'A chip used by this template is missing. Restore it before placing.';
    if (chip.templateType)
      return 'Templates must expand to contents, not reference another template.';
    const visited = new Set<string>();
    const pending = [chip];
    while (pending.length) {
      const dependency = pending.pop()!;
      if (visited.has(dependency.id)) continue;
      visited.add(dependency.id);
      for (const instance of dependency.logicCircuit.chipInstances.values()) {
        const nested = context.chipStore.getChip(instance.chipId);
        if (!nested)
          return 'A nested chip used by this template is missing. Restore it before placing.';
        if (nested.templateType)
          return 'Templates must expand to contents, not reference another template.';
        pending.push(nested);
      }
    }
    const sourceOrigin = Point.fromData(chip.structureCircuit.originLocation);
    const offset = Point.fromData(ref.relativeOrigin).sub(sourceOrigin);
    const expectedCells = chip.structureCircuit.circuitModel.tiles
      .map((tile) => {
        const point = rotatePointAroundOrigin(
          Point.fromData(tile.location),
          sourceOrigin,
          ref.rotation ?? Rotation.CW_0,
        ).add(offset);
        const link =
          (
            tile.state as
              | { connectedInterface?: { interfaceLinkId?: string } }
              | undefined
          )?.connectedInterface?.interfaceLinkId ?? '';
        return `${point.x},${point.y}:${tile.tileType}:${link}`;
      })
      .sort();
    const capturedCells = data.tiles
      .filter(
        (entry) =>
          entry.meta.partOfChip &&
          parseClipChipRef(entry.meta.partOfChip) === ref.localIndex,
      )
      .map((entry) => {
        const link =
          (
            entry.state as
              | { connectedInterface?: { interfaceLinkId?: string } }
              | undefined
          )?.connectedInterface?.interfaceLinkId ?? '';
        return `${entry.relativeLocation.x},${entry.relativeLocation.y}:${entry.tileType}:${link}`;
      })
      .sort();
    if (
      expectedCells.length !== capturedCells.length ||
      expectedCells.some((cell, index) => cell !== capturedCells[index])
    )
      return 'A referenced chip footprint has changed. Update the template before placing.';

    const result = getChipSelectability(chip, editingChip, context);
    if (!result.allowed)
      return result.reason ?? 'A referenced chip cannot be placed here.';
  }
  if (restrictChallengeInterfacePaste(context, data).tiles.length !== data.tiles.length)
    return 'This template contains interfaces that cannot be placed here.';
  const manager = circuit.tileManagerModel;
  const occupied = new Set<string>();
  for (const entry of data.tiles) {
    const point = new Point(
      origin.x + entry.relativeLocation.x,
      origin.y + entry.relativeLocation.y,
    );
    if (
      !isValidTilePosition(point.x, point.y) ||
      !allowsChallengePlacement(context, manager, point)
    )
      return 'The complete template must fit inside the circuit boundary.';
    if (manager.has(point)) return TEMPLATE_COLLISION_MESSAGE;
    const key = `${point.x},${point.y}`;
    if (occupied.has(key)) return 'The template contains overlapping cells.';
    occupied.add(key);
    if (entry.meta.partOfChip) {
      const index = parseClipChipRef(entry.meta.partOfChip);
      if (index === null || !indices.has(index))
        return 'The template contains an unresolved chip reference.';
    } else if (!allowsChallengeTile(context, manager, entry.tileType, entry.state))
      return 'This template contains a tile that is unavailable here.';
  }
  return null;
}

export function createTemplateStampAction(
  context: Context,
  data: ClipboardData,
  origin: PointData,
  circuit: ICircuitModel,
  chipEditor: IChipEditorModel,
  type: ProjectTemplateType,
) {
  if (
    getTemplatePlacementError(context, data, origin, circuit, type, chipEditor.chipModel)
  )
    return null;
  const captured = sanitizeTemplateClipboard(data);
  const action = createPasteAction(
    context,
    captured,
    templatePasteOrigin(captured, origin),
    circuit,
    chipEditor,
    {
      connectNeighbors: false,
      preferCopiedInterfaceSettings: true,
    },
  );
  if (!action) return null;
  let applied = false;
  return {
    ...action,
    description: 'Stamp template',
    do: () => {
      // Worker-owned edits can wait behind another command. Recheck the entire
      // stamp at execution time before the paste action can mutate any cell.
      const error = getTemplatePlacementError(
        context,
        captured,
        origin,
        circuit,
        type,
        chipEditor.chipModel,
      );
      if (error) {
        context.editor.templatePlacementMessage = error;
        applied = false;
        return;
      }
      action.do();
      applied = true;
    },
    undo: () => {
      if (applied) action.undo();
      applied = false;
    },
  };
}
